// frontend/src/charts/tableOrder.js
// Column order and sort targets for the table visual (Phase 3 step 7d-2,
// decision D11). Pure, shared by the app's DataTable and, from step 10b, the
// embed. A saved chart's presentation.columns lists the chart's own items
// ({field} for a grouping, {aggregation, field} for a value) in display
// order; it never changes the query, so a stale or partial list is harmless:
// matched columns come first in that order, the rest follow as the server
// returned them.

/** The chart-item form of a result column: what sorts and presentation.columns name. */
export function columnTarget(column) {
  if (!column) return null;
  if (column.kind === 'measure') {
    return column.aggregation === 'count' || !column.field
      ? { aggregation: column.aggregation }
      : { aggregation: column.aggregation, field: column.field };
  }
  return { field: column.field };
}

/** True when two targets name the same item (a grouping by field; a value by aggregation + field). */
export function sameTarget(a, b) {
  if (!a || !b) return false;
  return (a.aggregation || null) === (b.aggregation || null) && (a.field || null) === (b.field || null);
}

/** The key a column is known by in the builder's order and width lists (same form as tableColumns.columnKey). */
export function targetKey(target) {
  if (!target) return null;
  return target.aggregation ? `m:${target.aggregation}:${target.field || ''}` : `d:${target.field}`;
}

// A dragged column width in pixels (7d-3). Mirrors MIN/MAX_COLUMN_WIDTH in
// backend/app/semantic/presentation.py.
export const MIN_COLUMN_WIDTH = 40;
export const MAX_COLUMN_WIDTH = 1200;

/** A width the user dragged, kept within the limits as a whole number; null when not a usable width. */
export function clampWidth(w) {
  if (typeof w !== 'number' || !Number.isFinite(w)) return null;
  return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(w)));
}

/** { [targetKey]: width } from presentation.columns; entries without a usable width are left out. */
export function columnWidths(presentationColumns) {
  const out = {};
  for (const c of Array.isArray(presentationColumns) ? presentationColumns : []) {
    const w = c && clampWidth(c.width);
    if (w != null && w === c.width) out[targetKey(c)] = w;
  }
  return out;
}

/** Result columns in the presentation's order; unmatched entries are ignored, unmatched columns go last. */
export function orderColumns(columns, presentationColumns) {
  const cols = columns || [];
  if (!Array.isArray(presentationColumns) || presentationColumns.length === 0) return cols;
  const used = new Set();
  const first = [];
  for (const want of presentationColumns) {
    const i = cols.findIndex((c, idx) => !used.has(idx) && sameTarget(columnTarget(c), want));
    if (i >= 0) { used.add(i); first.push(cols[i]); }
  }
  return [...first, ...cols.filter((_, idx) => !used.has(idx))];
}